import React, { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { CHARGED_NOW_BY_REQUEST_NOTE } from '../../constants/byRequestCopy';
import { CART_CHECKOUT_PATH } from '../../constants/cartRoutes';
import { QUOTE_TO_FOLLOW_LABEL, formatAwaitingQuoteCount } from '../../constants/chargeTimingCopy';
import { CHANGE_CART_LINE_LABEL } from '../../constants/checkoutFlowCopy';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthPrompt } from '../../contexts/AuthPromptContext';
import { CartItem, deriveChargeTiming, useCart } from '../../contexts/CartContext';
import { FILLS, FONTS, RADII, STROKES } from '../../styles/tokens';
import { deriveCartItemDisplay } from '../../utils/cartItemDisplay';
import { formatCartLinePrice, formatCurrency } from '../../utils/formatters';
import CartLineExtras from './CartLineExtras';
import ChargeTimingChip from './ChargeTimingChip';

interface CartDropdownProps {
  hideIcon?: boolean;
}

const CART_ICON_PATH =
  'M7 18c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z';

const CartDropdown: React.FC<CartDropdownProps> = ({ hideIcon = false }) => {
  const {
    cartItems,
    removeFromCart,
    getTotalPrice,
    getItemCount,
    getChargeTimingGroups,
    isCartDrawerOpen,
    openCartDrawer,
    closeCartDrawer,
  } = useCart();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { openAuthPrompt } = useAuthPrompt();
  const containerRef = useRef<HTMLDivElement>(null);
  const cartTitleRef = useRef<HTMLHeadingElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const itemCount = getItemCount();
  const totalPrice = getTotalPrice();
  // By-request lines have no chargeable price yet — grouped separately
  // under their own "Quote to follow" heading rather than interleaved with
  // priced lines, and excluded from `pricedCartItems` so they render once.
  const awaitingQuoteItems = getChargeTimingGroups().awaitingQuote;
  const hasAwaitingQuoteItems = awaitingQuoteItems.length > 0;
  const pricedCartItems = cartItems.filter((item) => !awaitingQuoteItems.includes(item));
  const hasPricedItems = pricedCartItems.length > 0;
  // True when every line in the cart is by-request — the footer's priced
  // total would otherwise render a misleading "$0".
  const isAwaitingQuoteOnlyCart = hasAwaitingQuoteItems && !hasPricedItems;

  // Focus management — focus the title when the popup opens, restore on close.
  useEffect(() => {
    if (isCartDrawerOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      setTimeout(() => {
        cartTitleRef.current?.focus();
      }, 100);
    } else if (previousFocusRef.current) {
      previousFocusRef.current.focus();
    }
  }, [isCartDrawerOpen]);

  // Click-outside / Escape to close the popup.
  useEffect(() => {
    if (!isCartDrawerOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        closeCartDrawer();
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeCartDrawer();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [isCartDrawerOpen, closeCartDrawer]);

  const handleCheckoutAll = () => {
    if (cartItems.length === 0) return;
    if (!isAuthenticated) {
      closeCartDrawer();
      openAuthPrompt({ intent: 'checkout' });
      return;
    }
    navigate(CART_CHECKOUT_PATH);
    closeCartDrawer();
  };

  const handleViewFullCart = () => {
    navigate('/cart');
    closeCartDrawer();
  };

  // hideIcon callers (Layout-level mount) used to render a redundant drawer; with
  // the anchored popup pattern there's nothing for them to render.
  if (hideIcon) return null;

  const renderCartItemRow = (item: CartItem) => {
    const { name, variantName, meta, image, listingPath } = deriveCartItemDisplay(item);
    const chargeTiming = deriveChargeTiming(item);
    return (
      <div
        key={item.id}
        data-testid={`cart-item-${item.id}`}
        style={{
          display: 'flex',
          gap: '12px',
          padding: '14px 18px',
          borderBottom: `1px solid ${STROKES.subtle}`,
          alignItems: 'center',
        }}
      >
        <div
          style={{
            width: '56px',
            height: '56px',
            borderRadius: RADII.control,
            flexShrink: 0,
            background: `url(${image}) center/cover no-repeat`,
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h5
            style={{
              fontFamily: FONTS.display,
              fontSize: '15px',
              fontWeight: 500,
              color: FILLS.textPrimary,
              margin: '0 0 2px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {name}
          </h5>
          {variantName && (
            <p
              style={{
                fontFamily: FONTS.body,
                fontSize: '10px',
                fontWeight: 600,
                color: FILLS.gold,
                letterSpacing: '0.04em',
                margin: '0 0 2px',
              }}
            >
              {variantName}
            </p>
          )}
          {meta && (
            <p
              style={{
                fontFamily: FONTS.body,
                fontSize: '11px',
                color: FILLS.textMuted,
                margin: '0 0 4px',
              }}
            >
              {meta}
            </p>
          )}
          <CartLineExtras extras={item.selectedExtras ?? []} />
          <div
            style={{
              fontFamily: FONTS.body,
              fontSize: '13px',
              fontWeight: 600,
              color: FILLS.gold,
            }}
          >
            {formatCartLinePrice(item.totalPrice)}
          </div>
          <ChargeTimingChip timing={chargeTiming} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {listingPath !== null && (
            <Link
              to={listingPath}
              onClick={closeCartDrawer}
              aria-label={`${CHANGE_CART_LINE_LABEL} ${name}`}
              style={{
                fontFamily: FONTS.body,
                fontSize: '12px',
                color: FILLS.gold,
                textDecoration: 'none',
              }}
            >
              {CHANGE_CART_LINE_LABEL}
            </Link>
          )}
          <button
            type="button"
            onClick={() => removeFromCart(item.id)}
            aria-label={`Remove ${name} from cart`}
            style={{
              background: 'transparent',
              border: 'none',
              color: FILLS.textMuted,
              cursor: 'pointer',
              fontSize: '16px',
              padding: '4px',
              lineHeight: 1,
              transition: 'color 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = FILLS.error;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = FILLS.textMuted;
            }}
          >
            ×
          </button>
        </div>
      </div>
    );
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        onClick={() => (isCartDrawerOpen ? closeCartDrawer() : openCartDrawer())}
        aria-label={`Shopping cart with ${itemCount} items`}
        aria-haspopup="dialog"
        aria-expanded={isCartDrawerOpen}
        style={{
          position: 'relative',
          width: '38px',
          height: '38px',
          borderRadius: '50%',
          background: 'transparent',
          border: `1px solid ${STROKES.subtle}`,
          color: FILLS.textLight,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.2s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = FILLS.goldAlpha30;
          e.currentTarget.style.color = FILLS.gold;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = STROKES.subtle;
          e.currentTarget.style.color = FILLS.textLight;
        }}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
          style={{ display: 'block' }}
        >
          <path d={CART_ICON_PATH} />
        </svg>
        {itemCount > 0 && (
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: '-4px',
              right: '-4px',
              minWidth: '18px',
              height: '18px',
              padding: '0 5px',
              borderRadius: '9px',
              background: FILLS.gold,
              color: FILLS.page,
              fontFamily: FONTS.body,
              fontSize: '10px',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
            }}
          >
            {itemCount}
          </span>
        )}
      </button>

      {isCartDrawerOpen && (
        <div
          role="dialog"
          aria-labelledby="cart-pop-title"
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            width: 'min(360px, calc(100vw - 24px))',
            background: FILLS.surface,
            border: `1px solid ${STROKES.subtle}`,
            borderRadius: RADII.card,
            overflow: 'hidden',
            boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
            zIndex: 200,
          }}
        >
          {/* Head */}
          <div
            style={{
              padding: '14px 18px',
              borderBottom: `1px solid ${STROKES.subtle}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <h4
              id="cart-pop-title"
              ref={cartTitleRef}
              tabIndex={-1}
              style={{
                fontFamily: FONTS.body,
                fontSize: '14px',
                fontWeight: 600,
                color: FILLS.textPrimary,
                margin: 0,
                outline: 'none',
              }}
            >
              Your Cart
            </h4>
            <span
              style={{
                fontFamily: FONTS.body,
                fontSize: '11px',
                color: FILLS.textMuted,
              }}
            >
              {itemCount} {itemCount === 1 ? 'item' : 'items'}
            </span>
          </div>

          {/* Items */}
          {cartItems.length === 0 ? (
            <div
              style={{
                padding: '32px 18px',
                textAlign: 'center',
                fontFamily: FONTS.body,
                fontSize: '13px',
                color: FILLS.textMuted,
              }}
            >
              Your cart is empty.
            </div>
          ) : (
            <div
              style={{
                maxHeight: '320px',
                overflowY: 'auto',
              }}
            >
              {pricedCartItems.map(renderCartItemRow)}
              {hasAwaitingQuoteItems && (
                <>
                  <h4
                    style={{
                      padding: '10px 18px 6px',
                      margin: 0,
                      fontFamily: FONTS.body,
                      fontSize: '11px',
                      fontWeight: 600,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: FILLS.textMuted,
                    }}
                  >
                    {QUOTE_TO_FOLLOW_LABEL}
                  </h4>
                  {awaitingQuoteItems.map(renderCartItemRow)}
                </>
              )}
            </div>
          )}

          {/* Footer */}
          {cartItems.length > 0 && (
            <div
              style={{
                padding: '14px 18px',
                borderTop: `1px solid ${STROKES.subtle}`,
              }}
            >
              {!isAwaitingQuoteOnlyCart && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontFamily: FONTS.body,
                    fontSize: '14px',
                    color: FILLS.textPrimary,
                    marginBottom: '12px',
                  }}
                >
                  <span>
                    <span>Total</span>
                    {hasAwaitingQuoteItems && (
                      <span style={{ marginLeft: '8px', fontSize: '11px', color: FILLS.textMuted }}>
                        {formatAwaitingQuoteCount(awaitingQuoteItems.length)}
                      </span>
                    )}
                  </span>
                  <span style={{ fontWeight: 600 }}>{formatCurrency(totalPrice)}</span>
                </div>
              )}
              {hasAwaitingQuoteItems && (
                <p
                  style={{
                    fontFamily: FONTS.body,
                    fontSize: '11px',
                    color: FILLS.textMuted,
                    lineHeight: 1.5,
                    margin: '0 0 12px',
                  }}
                >
                  {CHARGED_NOW_BY_REQUEST_NOTE}
                </p>
              )}
              <button
                type="button"
                onClick={handleCheckoutAll}
                aria-label={
                  isAwaitingQuoteOnlyCart
                    ? `Checkout all items`
                    : `Checkout all items for ${formatCurrency(totalPrice)}`
                }
                style={{
                  width: '100%',
                  padding: '16px',
                  border: 'none',
                  borderRadius: RADII.control,
                  background: FILLS.gold,
                  color: FILLS.page,
                  fontFamily: FONTS.body,
                  fontSize: '15px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.25s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = FILLS.goldWarm;
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = FILLS.gold;
                  e.currentTarget.style.transform = 'none';
                }}
              >
                Checkout
              </button>
              <button
                type="button"
                onClick={handleViewFullCart}
                style={{
                  width: '100%',
                  padding: '10px',
                  marginTop: '4px',
                  border: 'none',
                  borderRadius: RADII.control,
                  background: 'transparent',
                  color: FILLS.textMuted,
                  fontFamily: FONTS.body,
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'color 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = FILLS.textPrimary;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = FILLS.textMuted;
                }}
              >
                View full cart
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CartDropdown;
